import React, { memo, Suspense, useCallback, useMemo, useState } from 'react';
import times from 'lodash/times';

import { useTranslation } from '@lyearn/i18n';
import { AlertTriangleFill, Close, DiamondOutline, PuzzleOutline } from '@lyearn/icons';
import { AddBlock, AlertDialog, TypographyWithTooltip } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Box, LinkButton, Loader, Popover, Skeleton, Stack, Typography } from '@lyearn/ui';
import {
  MindsetEntityConfig,
  SkillEntityConfig,
} from '@/components/FilterManager/EntityFilter/entity/skills';
import FilterBody from '@/components/FilterNew/components/Body';
import { NewFilterOption } from '@/components/FilterNew/types';
import { useSkillsLightQuery } from '@/modules/corporate/modules/admin/views/AICourseCreation/graphql/queries/generated/skillsLight';
import { SkillTypeEnum } from '@/types/schema';

import { AttachSkillsContentProps, AttachSkillsUiState } from './type';

const translationMap = {
  [SkillTypeEnum.Ability]: {
    Title: 'Common:Form.Skills',
    Add: 'Skills:AttachSkills.AddSkills',
    AttachActionLabel: 'Skills:AttachSkills.AttachActionLabel',
    AttachTitle: 'Skills:AttachSkills.AttachTitle',
    AttachConfirm: 'Skills:AttachSkills.AttachConfirm',
    DeleteActionLabel: 'Skills:AttachSkills.DeleteActionLabel',
    DeleteTitle: 'Skills:AttachSkills.DeleteTitle',
    DeleteConfirm: 'Skills:AttachSkills.DeleteConfirm',
    ChangeCallout: 'Skills:AttachSkills.ChangeCallout',
  },
  [SkillTypeEnum.Behavior]: {
    Title: 'Common:Form.Mindsets',
    Add: 'Skills:AttachMindsets.AddMindsets',
    AttachActionLabel: 'Skills:AttachMindsets.AttachActionLabel',
    AttachTitle: 'Skills:AttachMindsets.AttachTitle',
    AttachConfirm: 'Skills:AttachMindsets.AttachConfirm',
    DeleteActionLabel: 'Skills:AttachMindsets.DeleteActionLabel',
    DeleteTitle: 'Skills:AttachMindsets.DeleteTitle',
    DeleteConfirm: 'Skills:AttachMindsets.DeleteConfirm',
    ChangeCallout: 'Skills:AttachMindsets.ChangeCallout',
  },
};

const entityConfigMap: Record<SkillTypeEnum, typeof SkillEntityConfig> = {
  [SkillTypeEnum.Ability]: SkillEntityConfig,
  [SkillTypeEnum.Behavior]: MindsetEntityConfig,
};

const AttachSkillsContent: React.FC<AttachSkillsContentProps> = (props) => {
  const {
    selectedSkillIds,
    skillType,
    onChange,
    disabled = false,
    readOnly = false,
    showConfirmBeforeAdd = true,
    showConfirmBeforeRemove = true,
    showChangeCallout = true,
  } = props;
  const { t } = useTranslation(['Common', 'Skills']);

  const [uiState, setUiState] = useState<AttachSkillsUiState | null>(null);
  const showConfirmDeleteModal = useCallback((skillId: string) => {
    setUiState({
      type: 'confirm-delete',
      payload: {
        skillId,
      },
    });
  }, []);
  const showConfirmAddModal = useCallback((skillIds: string[]) => {
    setUiState({
      type: 'confirm-add',
      payload: {
        skillIds,
      },
    });
  }, []);
  const closeModal = useCallback(() => setUiState(null), []);

  const [{ data: selectedSkillData, fetching: isFetching }] = useSkillsLightQuery({
    variables: {
      filters: {
        ids: selectedSkillIds,
        types: [skillType],
      },
    },
    pause: !selectedSkillIds.length,
  });

  const selectedSkills = useMemo(
    () =>
      selectedSkillIds.length > 0
        ? selectedSkillData?.skills?.edges?.map((edge) => edge.node) ?? []
        : [],
    [selectedSkillData?.skills?.edges, selectedSkillIds.length],
  );

  const onRemove = (skillId: string) => {
    const newSkillIds = selectedSkillIds.filter((id) => id !== skillId);
    onChange(newSkillIds);
  };

  const onRemoveSkillClick = (skillId: string) => {
    if (disabled) {
      return;
    }
    if (showConfirmBeforeRemove) {
      showConfirmDeleteModal(skillId);
    } else {
      onRemove(skillId);
    }
  };

  const onAdd = (skillIds: string[]) => {
    onChange(skillIds);
  };

  const onAddSkillClick = (values: NewFilterOption[]) => {
    if (disabled) {
      return;
    }
    const skillIds = values.map((v) => v.value);
    if (showConfirmBeforeAdd) {
      showConfirmAddModal(skillIds);
    } else {
      onAdd(skillIds);
    }
  };

  const entityConfig = entityConfigMap[skillType];
  const selectedFilterValuesData = entityConfig.useFilterValues({ value: selectedSkillIds });

  return (
    <>
      <Suspense fallback={<Loader />}>
        {showChangeCallout ? (
          <Stack
            alignItems="center"
            className="bg-bg-warning-light px-16 py-10 mb-24"
            direction="row"
            gap={12}>
            <AlertTriangleFill
              className="shrink-0"
              fillColorToken="text-warning-dark"
              height={16}
              width={16}
            />
            <Typography color="text-text-warning-dark" variant="body-short-02">
              {t(translationMap[skillType].ChangeCallout)}
            </Typography>
          </Stack>
        ) : null}
        <Stack className={cn('flex-1 flex-wrap')} direction="row" gap={12}>
          {isFetching
            ? times(selectedSkillIds.length, (index) => (
                <Box
                  key={index}
                  className={cn(
                    'flex h-40 max-w-full items-center justify-between truncate rounded-8 border px-12 border-divider',
                    { 'pointer-events-none bg-bg-secondary opacity-50': disabled },
                  )}>
                  <Stack alignItems="center" className="truncate">
                    {skillType == SkillTypeEnum.Ability ? (
                      <PuzzleOutline
                        className="h-18 w-18 shrink-0"
                        fillColorToken="icon-secondary"
                      />
                    ) : (
                      <DiamondOutline
                        className="h-18 w-18 shrink-0"
                        fillColorToken="icon-secondary"
                      />
                    )}
                    <Typography
                      className="mx-8 truncate"
                      color="text-text-secondary"
                      variant="body-short-02">
                      <Skeleton className="mx-8 w-[10rem]" variant="text" />
                    </Typography>
                  </Stack>
                </Box>
              ))
            : null}
          {selectedSkills.map((skill) => {
            return (
              <Box
                key={skill.id}
                className={cn(
                  'flex h-40 max-w-full items-center justify-between truncate rounded-8 border px-12 border-divider',
                  { 'pointer-events-none bg-bg-secondary opacity-50': disabled },
                )}>
                <Stack alignItems="center" className="truncate">
                  {skillType == SkillTypeEnum.Ability ? (
                    <PuzzleOutline className="h-18 w-18 shrink-0" fillColorToken="icon-secondary" />
                  ) : (
                    <DiamondOutline
                      className="h-18 w-18 shrink-0"
                      fillColorToken="icon-secondary"
                    />
                  )}
                  <TypographyWithTooltip
                    className="mx-8 truncate"
                    color="text-text-secondary"
                    numberOfLines={1}
                    variant="body-short-02">
                    {skill.name}
                  </TypographyWithTooltip>
                </Stack>
                {disabled || readOnly ? null : (
                  <LinkButton
                    className="group flex-shrink-0 py-2 px-4 ps-0 pe-0 ms-8"
                    color={'internal'}
                    onClick={() => onRemoveSkillClick(skill.id)}>
                    <Close className="h-20 w-20 text-icon-placeholder group-hover:text-icon-primary" />
                  </LinkButton>
                )}
              </Box>
            );
          })}
          {readOnly ? null : (
            <Popover>
              {({ open, onOpenChange }) => (
                <>
                  <Popover.Button disabled={disabled}>
                    <AddBlock length="short" title={t(translationMap[skillType].Add)} />
                  </Popover.Button>
                  {open ? (
                    <Popover.Content position="bottom" renderAs="popover">
                      <FilterBody
                        initialSelectedOptions={selectedFilterValuesData.selectedOptions}
                        listConfig={entityConfig}
                        onChange={onAddSkillClick}
                        onToggle={onOpenChange}
                      />
                    </Popover.Content>
                  ) : null}
                </>
              )}
            </Popover>
          )}
        </Stack>
        {uiState?.type === 'confirm-delete' ? (
          <AlertDialog
            header={{
              title: t(translationMap[skillType].DeleteTitle),
              description: t(translationMap[skillType].DeleteConfirm),
            }}
            primaryAction={{
              appearance: 'danger',
              label: t(translationMap[skillType].DeleteActionLabel),
              onClick: () => {
                onRemove(uiState?.payload.skillId);
                closeModal();
              },
            }}
            onClose={closeModal}></AlertDialog>
        ) : null}
        {uiState?.type === 'confirm-add' ? (
          <AlertDialog
            header={{
              title: t(translationMap[skillType].AttachTitle),
              description: t(translationMap[skillType].AttachConfirm),
            }}
            primaryAction={{
              label: t(translationMap[skillType].AttachActionLabel),
              onClick: () => {
                onAdd(uiState?.payload.skillIds);
                closeModal();
              },
            }}
            onClose={closeModal}></AlertDialog>
        ) : null}
      </Suspense>
    </>
  );
};

export default memo(AttachSkillsContent);
